import { createEffect, type JSX, Match, Switch } from 'solid-js';
import { t } from '../i18n';
import { isForbidden } from '../lib/api';
import { DeniedState } from './DeniedState';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';

// The one loading / error / empty / data pattern every data route uses. Pass a
// query + the slots; it picks the state. Typed structurally (not against
// TanStack's generic) so any `{ isLoading, isError, error, data, refetch }`
// works — keeps the routes' query libs decoupled from this component.

export type QueryLike<T> = {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
  readonly refetch: () => unknown;
};

export type QueryBoundaryProps<T> = {
  readonly query: QueryLike<T>;
  /** Shown while fetching — typically a <CardGridSkeleton/>. */
  readonly loading: JSX.Element;
  /**
   * Shown when the query resolves to no data (per `isEmpty`) or stays idle.
   *
   * Optional, and DEFAULTED rather than allowed to be nothing: this slot used to
   * render `{undefined}` when a caller omitted it, so an idle or empty query
   * painted an empty `.page` box — no message, no retry, nothing to act on, and
   * no way for the reader to tell "nothing here" from "this failed". Nineteen
   * call sites were in that shape. Pass one to say something better than the
   * generic line; omitting it can no longer produce a blank screen.
   */
  readonly empty?: JSX.Element;
  /**
   * Override for the 403 branch. The branch is SELECTED from the error, never
   * opted into — omitting this gets `<DeniedState/>`, not the retryable error
   * box. An opt-in slot would have to be threaded through all 67 call sites to
   * work, and the 66 that had not been edited yet would keep offering a Retry
   * on an answer: exactly the defect `empty` above records, one state over.
   */
  readonly denied?: JSX.Element;
  readonly isEmpty?: (data: T) => boolean;
  readonly children: (data: T) => JSX.Element;
};

export function QueryBoundary<T>(props: QueryBoundaryProps<T>): JSX.Element {
  const hasData = (): boolean =>
    props.query.data !== undefined && !(props.isEmpty?.(props.query.data) ?? false);

  // The focus-recovery wrapper below is a real box in the page's flow, so it
  // has to carry the page's vertical rhythm too (#916). Without it a route that
  // renders SEVERAL blocks through the boundary — the per-scope panels on
  // /dashboards, say — has them stack flush against each other: `.page`'s gap
  // stops at this div and never reaches its children.
  //
  // Retry recovery: the ErrorState retry button unmounts with the error slot,
  // dropping keyboard focus to <body>. Land it on whichever region replaces the
  // error — data or the empty fallback (both carry the `recovered` ref).
  let recovered: HTMLDivElement | undefined;
  let wasError = false;
  createEffect(() => {
    const errored = props.query.isError;
    if (wasError && !errored) {
      queueMicrotask(() => {
        if (document.activeElement === document.body) recovered?.focus();
      });
    }
    wasError = errored;
  });

  return (
    <Switch
      fallback={
        <div class="page" ref={recovered} tabindex="-1">
          {props.empty ?? <EmptyState message={t('states.empty')} />}
        </div>
      }
    >
      <Match when={props.query.isLoading}>
        {/* Announce loading — skeletons are invisible to assistive tech. Real
            (sr-only) text, not aria-label: role=status announcements are
            driven by text-content changes. */}
        <div role="status">
          <span class="sr-only">{t('states.loading')}</span>
          {props.loading}
        </div>
      </Match>
      {/* ORDER IS LOAD-BEARING: <Switch> takes the FIRST match, so the 403 arm
          must sit above the general error arm — below it, every denial falls
          through to `<ErrorState/>` and gets the Retry that is the whole point
          of splitting them. `QueryBoundary.test.tsx` pins the precedence rather
          than only the rendering, because both arms match an errored query and
          nothing else would notice the swap. */}
      <Match when={props.query.isError && isForbidden(props.query.error)}>
        {/* Carries `recovered` for the same reason the empty slot does: a denial
            can be replaced by data (an org switch, a granted scope) and the
            focus dropped by the unmounting error box has to land somewhere. */}
        <div class="page" ref={recovered} tabindex="-1">
          {props.denied ?? <DeniedState error={props.query.error} />}
        </div>
      </Match>
      <Match when={props.query.isError}>
        <ErrorState error={props.query.error} onRetry={() => void props.query.refetch()} />
      </Match>
      <Match when={hasData()}>
        <div class="page" ref={recovered} tabindex="-1">
          {props.children(props.query.data as T)}
        </div>
      </Match>
    </Switch>
  );
}
